import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Loader2, CheckCircle2 } from 'lucide-react';
import { contactAPI, authAPI } from '../services/api';
import { useT } from '../services/i18n';

const errText = (e, fb) => { const d = e.response?.data?.detail; return Array.isArray(d) ? d.map(x => x.msg).join(' ') : d || fb; };

// Floating chat bubble → mini contact form; messages land in the admin "Messages" inbox with the visited page
const ChatWidget = () => {
  const { t } = useT();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (isOpen && authAPI.isAuthenticated() && !form.email) {
      authAPI.getProfile().then(p => setForm(f => ({ ...f, name: `${p.first_name} ${p.last_name}`.trim(), email: p.email }))).catch(() => {});
    }
  }, [isOpen, form.email]);

  if (location.pathname.startsWith('/admin')) return null;

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.message.trim().length < 10) { setError(t('chat.tooShort')); return; }
    setSending(true);
    try {
      const r = await contactAPI.send({ name: form.name, email: form.email, subject: 'Autre', message: form.message, page_url: window.location.href, source: 'chat' });
      setDone(r);
      setForm(f => ({ ...f, message: '' }));
    } catch (err) {
      setError(errText(err, t('chat.failed')));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50" data-testid="chat-widget">
      {isOpen && (
        <div className="absolute bottom-16 right-0 w-[min(20rem,calc(100vw-3rem))] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden animate-in slide-in-from-bottom-4 duration-300" data-testid="chat-window">
          <div className="bg-[#1e3a5f] text-white p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center"><MessageCircle size={20} /></div>
                <div><p className="font-medium">{t('header.customerService')}</p><p className="text-xs text-gray-300">{t('chat.replyTime')}</p></div>
              </div>
              <button type="button" onClick={() => setIsOpen(false)} data-testid="chat-close" className="p-1 hover:bg-white/10 rounded transition-colors"><X size={18} /></button>
            </div>
          </div>

          {done ? (
            <div className="p-6 text-center" data-testid="chat-success">
              <CheckCircle2 size={40} className="mx-auto text-green-500 mb-2" />
              <p className="font-semibold text-gray-900">{t('chat.sent')}</p>
              <p className="text-sm text-gray-600 mt-1">{t('chat.reference')} <strong data-testid="chat-ticket">{done.ticket}</strong>. Nous vous répondons à {form.email}.</p>
              <button type="button" onClick={() => setDone(null)} data-testid="chat-new" className="mt-4 text-sm text-orange-600 hover:underline">{t('chat.another')}</button>
            </div>
          ) : (
            <form onSubmit={submit} className="p-4 space-y-3 bg-gray-50" data-testid="chat-form">
              <div className="bg-white p-3 rounded-lg shadow-sm text-sm text-gray-700">{t('chat.greeting')}</div>
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="chat-name" required minLength={2} placeholder={t('chat.namePh')} value={form.name} onChange={set('name')} className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
                <input data-testid="chat-email" required type="email" placeholder={t('chat.emailPh')} value={form.email} onChange={set('email')} className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
              <textarea data-testid="chat-message" required rows={3} placeholder={t('chat.messagePh')} value={form.message} onChange={set('message')} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
              {error && <p className="text-xs text-red-600" data-testid="chat-error">{error}</p>}
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] text-gray-400 truncate">{t('chat.page')} {location.pathname}</p>
                <button type="submit" disabled={sending} data-testid="chat-send" className="bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white px-3 py-2 rounded-md transition-colors inline-flex items-center gap-1 text-sm font-medium">{sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} {t('chat.send')}</button>
              </div>
            </form>
          )}
        </div>
      )}

      <button type="button" onClick={() => setIsOpen(!isOpen)} data-testid="chat-toggle" aria-label={t('chat.open')}
        className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 ${isOpen ? 'bg-gray-700 rotate-90' : 'bg-orange-500 hover:bg-orange-600'}`}>
        {isOpen ? <X size={24} className="text-white" /> : <MessageCircle size={24} className="text-white" />}
      </button>
    </div>
  );
};

export default ChatWidget;
